import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { dispatchService, Dispatch } from '../services/dispatchService';
import { Dialog } from '@headlessui/react';
import {
  XMarkIcon,
  TruckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface DriverDeliveryScreenProps {}

const DriverDeliveryScreen: React.FC<DriverDeliveryScreenProps> = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispatch, setSelectedDispatch] = useState<Dispatch | null>(null);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [otp, setOtp] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [requestingOTP, setRequestingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [otpData, setOtpData] = useState<{ otp: string; expiresAt: string } | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadDispatches();
    }
  }, [user]);

  const loadDispatches = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const response = await dispatchService.getDriverDispatches(user.id);
      if (response.success && response.data) {
        setDispatches(response.data);
      } else {
        toast.error('Failed to load dispatches');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load dispatches');
    } finally {
      setLoading(false);
    }
  };

  const handleDeliver = (dispatch: Dispatch) => {
    setSelectedDispatch(dispatch);
    setShowOTPDialog(true);
    setOtp('');
    setCustomerMobile('');
    setOtpData(null);
  };

  const handleRequestOTP = async () => {
    if (!selectedDispatch || !customerMobile) {
      toast.error('Please enter customer mobile number');
      return;
    }

    setRequestingOTP(true);
    try {
      const response = await dispatchService.requestOTP({
        dispatchId: selectedDispatch.id,
        customerMobile,
      });

      if (response.success && response.data) {
        setOtpData(response.data);
        toast.success('OTP generated successfully. Please share with customer.');
      } else {
        toast.error(response.error?.message || 'Failed to generate OTP');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate OTP');
    } finally {
      setRequestingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!selectedDispatch || !otp) {
      toast.error('Please enter OTP');
      return;
    }

    if (otp.length !== 6) {
      toast.error('OTP must be 6 digits');
      return;
    }

    setVerifyingOTP(true);
    try {
      const response = await dispatchService.verifyOTP({
        dispatchId: selectedDispatch.id,
        otp,
      });

      if (response.success && response.data) {
        toast.success(`Successfully delivered ${response.data.deliveredItems} items!`);
        setShowOTPDialog(false);
        setSelectedDispatch(null);
        setOtp('');
        setCustomerMobile('');
        setOtpData(null);
        loadDispatches(); // Refresh list
      } else {
        toast.error(response.error?.message || 'Invalid OTP');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to verify OTP');
    } finally {
      setVerifyingOTP(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_transit':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dispatches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Driver Delivery</h1>
          <p className="text-gray-600 mt-2">Manage your assigned dispatches</p>
        </div>

        {/* Dispatches List */}
        {dispatches.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <TruckIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No dispatches assigned</p>
          </div>
        ) : (
          <div className="space-y-4">
            {dispatches.map((dispatch) => (
              <div
                key={dispatch.id}
                className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <TruckIcon className="w-6 h-6 text-blue-600" />
                      <h3 className="text-lg font-semibold">Vehicle: {dispatch.vehicle_no}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(dispatch.status)}`}>
                        {dispatch.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Dispatch Date: {new Date(dispatch.dispatch_date).toLocaleString()}</p>
                      {dispatch.driver && (
                        <p>
                          Driver: {dispatch.driver.first_name} {dispatch.driver.last_name}
                        </p>
                      )}
                      <p>Items: {dispatch.items.length}</p>
                    </div>
                  </div>
                  {dispatch.status !== 'delivered' && (
                    <button
                      onClick={() => handleDeliver(dispatch)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Deliver
                    </button>
                  )}
                </div>

                {/* Items List */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Items:</h4>
                  <div className="space-y-2">
                    {dispatch.items.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <div className="text-sm font-medium">{item.qr_code}</div>
                          <div className="text-xs text-gray-600">
                            {item.customer_name} • {item.product_name} • Qty: {item.quantity}
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* OTP Dialog */}
        <Dialog open={showOTPDialog} onClose={() => setShowOTPDialog(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="mx-auto max-w-md w-full rounded-xl bg-white p-6">
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title className="text-xl font-semibold">Delivery Verification</Dialog.Title>
                <button
                  onClick={() => setShowOTPDialog(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {selectedDispatch && (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm">
                      <strong>Vehicle:</strong> {selectedDispatch.vehicle_no}
                    </p>
                    <p className="text-sm">
                      <strong>Items:</strong> {selectedDispatch.items.length}
                    </p>
                  </div>

                  {!otpData ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Customer Mobile <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={customerMobile}
                            onChange={(e) => setCustomerMobile(e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Enter customer mobile"
                          />
                          <button
                            onClick={handleRequestOTP}
                            disabled={!customerMobile || requestingOTP}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${
                              !customerMobile || requestingOTP
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                          >
                            {requestingOTP ? 'Requesting...' : 'Request OTP'}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <KeyIcon className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-blue-800">OTP Generated</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-900 mb-2">{otpData.otp}</div>
                        <p className="text-xs text-blue-700">
                          Expires at: {new Date(otpData.expiresAt).toLocaleString()}
                        </p>
                        <p className="text-sm text-blue-800 mt-2">
                          Please share this OTP with the customer
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Enter OTP from Customer <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-2xl tracking-widest"
                          placeholder="000000"
                          maxLength={6}
                        />
                      </div>

                      <div className="flex gap-3 pt-4">
                        <button
                          onClick={() => {
                            setOtpData(null);
                            setOtp('');
                          }}
                          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          Request New OTP
                        </button>
                        <button
                          onClick={handleVerifyOTP}
                          disabled={otp.length !== 6 || verifyingOTP}
                          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md ${
                            otp.length !== 6 || verifyingOTP
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          {verifyingOTP ? 'Verifying...' : 'Verify & Deliver'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default DriverDeliveryScreen;

